import React from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Tooltip } from "../../../../ui/components/Tooltip";
import * as SubframeCore from "@subframe/core";
import {
  AgentReviewStatus,
  STATUS_HELP,
  STATUS_LABEL,
} from "../../domain/entities/AgentReviewEntities";

/**
 * ZOUR-67 — the four statuses.
 *
 * Colour carries the meaning an accountant scans for: green is safe to post,
 * amber wants a person, neutral is "not yet", red is "the check itself broke".
 * The tooltip spells each one out, because "Not checked" and "Checking" are
 * easy to read as the same thing and they are not.
 */
const VARIANT: Record<AgentReviewStatus, "success" | "warning" | "neutral" | "error"> = {
  ready: "success",
  needs_attention: "warning",
  checking: "neutral",
  not_checked: "error",
};

interface Props {
  status: AgentReviewStatus;
  /** The bill moved since the run, so what is shown is about an older version. */
  stale?: boolean;
}

export const AgentReviewStatusBadge: React.FC<Props> = ({ status, stale }) => {
  const help = stale
    ? "This bill has changed since it was last reviewed. The background check will pick it up again."
    : STATUS_HELP[status];

  return (
    <SubframeCore.Tooltip.Provider>
      <SubframeCore.Tooltip.Root>
        <SubframeCore.Tooltip.Trigger asChild={true}>
          <span className="inline-flex">
            <Badge variant={VARIANT[status]}>{STATUS_LABEL[status]}</Badge>
          </span>
        </SubframeCore.Tooltip.Trigger>
        <SubframeCore.Tooltip.Portal>
          <SubframeCore.Tooltip.Content asChild={true} sideOffset={4}>
            <Tooltip>{help}</Tooltip>
          </SubframeCore.Tooltip.Content>
        </SubframeCore.Tooltip.Portal>
      </SubframeCore.Tooltip.Root>
    </SubframeCore.Tooltip.Provider>
  );
};

export default AgentReviewStatusBadge;
